import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import ImportWizard from '../components/ImportWizard';
import { supabase } from '../lib/supabase';
import { CATEGORIES } from '../lib/constants';

const { FiDownload, FiUpload, FiDatabase, FiShield, FiRefreshCw, FiLoader } = FiIcons;

const Settings = () => {
  const [showWizard, setShowWizard] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleFullExport = async () => {
    setExporting(true);
    try {
      const { data, error } = await supabase.from('inventory').select('*').order('inventory_id');
      
      if (error) throw error;

      if (!data || data.length === 0) {
        alert("Keine Daten zum Exportieren vorhanden.");
        return;
      }

      // Daten aufbereiten (Flattening von specs und Bilder-Array)
      const formatted = data.map(item => {
        const row = {
          'Inventar-ID': item.inventory_id,
          'Hersteller': item.manufacturer,
          'Artikelnummer': item.article_number,
          'Name': item.name,
          'Kategorie': CATEGORIES[item.category]?.label || item.category,
          'Zustand': item.condition,
          'Standort': item.location,
          'Anzahl': item.count,
          'Einkaufspreis': item.price_buy,
          'Katalogwert': item.price_catalog,
          'Gesamtwert': item.total_value,
          'Kaufdatum': item.date_buy,
          'Epoche': item.epoch,
          'Bahnverwaltung': item.railway,
          'System': item.system,
          'Digitaltyp': item.digital_type,
          'Sound': item.sound ? 'Ja' : 'Nein',
          'Notiz': item.note,
          'Bilder': (item.images || []).join(', ')
        };

        // Technische Details aus specs flach hinzufügen
        if (item.specs && typeof item.specs === 'object') {
          Object.entries(item.specs).forEach(([key, val]) => {
            row[`Tech_${key}`] = val;
          });
        }

        return row;
      });

      const ws = XLSX.utils.json_to_sheet(formatted);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Inventar");
      
      XLSX.writeFile(wb, `MoBa_Backup_Vollstaendig_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error("Backup Error:", err);
      alert("Fehler beim Erstellen des Backups: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <header>
        <h1 className="text-3xl font-black text-slate-800 tracking-tighter">Verwaltung & Datensicherung</h1>
        <p className="text-slate-500">Importieren, Exportieren und Verwalten Sie Ihre Sammlungsdaten.</p>
      </header>

      {showWizard ? (
        <ImportWizard onComplete={() => setShowWizard(false)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 transition-all hover:shadow-md">
            <div className="w-12 h-12 bg-yellow-50 text-yellow-600 rounded-xl flex items-center justify-center mb-6">
              <SafeIcon icon={FiUpload} className="text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Daten-Import</h3>
            <p className="text-sm text-slate-500 mb-6">Fügen Sie Objekte aus Excel- oder CSV-Dateien hinzu. Unser Assistent hilft Ihnen beim Mapping der Spalten.</p>
            <button 
              onClick={() => setShowWizard(true)}
              className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
            >
              Assistent starten
            </button>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 transition-all hover:shadow-md">
            <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-6">
              <SafeIcon icon={FiDownload} className="text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Voll-Backup</h3>
            <p className="text-sm text-slate-500 mb-6">Exportieren Sie Ihre gesamte Datenbank als Excel-Datei inkl. aller technischen Details und Metadaten.</p>
            <button 
              onClick={handleFullExport}
              disabled={exporting}
              className="w-full border-2 border-slate-100 text-slate-700 py-3 rounded-xl font-bold hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {exporting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiDownload} />}
              Excel Export (Alle)
            </button>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 opacity-50 cursor-not-allowed">
            <div className="w-12 h-12 bg-green-50 text-green-600 rounded-xl flex items-center justify-center mb-6">
              <SafeIcon icon={FiShield} className="text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Cloud Sync</h3>
            <p className="text-sm text-slate-500 mb-6">Synchronisieren Sie Ihre Bilder und Daten mit einem externen Cloud-Speicher (Demnächst).</p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 opacity-50 cursor-not-allowed">
            <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center mb-6">
              <SafeIcon icon={FiRefreshCw} className="text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Datenbereinigung</h3>
            <p className="text-sm text-slate-500 mb-6">Suchen und korrigieren Sie fehlerhafte Einträge oder verwaiste Bilder (Demnächst).</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;